import { Calendar, Clock } from 'lucide-react';

type InspectionDateFieldProps = {
  value: string;
  onChange: (value: string) => void;
  /** YYYY-MM-DD; defaults to today (local). */
  max?: string;
  id?: string;
};

function todayLocal(): string {
  return new Date().toLocaleDateString('en-CA');
}

function openNativePicker(input: HTMLInputElement | null) {
  if (!input) return;
  // showPicker needs a user gesture and is missing in some WebViews —
  // fall back to focusing the field so the native control still works.
  try {
    if (typeof input.showPicker === 'function') {
      input.showPicker();
      return;
    }
  } catch {
    /* user-gesture or cross-origin restriction: fall through */
  }
  input.focus();
}

type InspectionTimeFieldProps = {
  /** HH:MM (24h) in the device's zone. */
  value: string;
  onChange: (value: string) => void;
  id?: string;
};

/**
 * The inspection-time control next to the date (JIM-0919-04). Defaults to the
 * moment the inspection was opened; a back-dated visit gets an approximate
 * time here. Jim 2026-09-19: "5:15, 5:30, 5:45, that kind of thing… make it
 * easier on the phone for the user" — so the native picker steps by 15 min.
 */
export function InspectionTimeField({
  value,
  onChange,
  id = 'lite-conducted-time',
}: InspectionTimeFieldProps) {
  return (
    <div className="relative inline-flex items-center">
      <Clock
        aria-hidden="true"
        className="pointer-events-none absolute left-2 w-3.5 h-3.5 text-slate-400"
      />
      <input
        id={id}
        type="time"
        step={900}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => openNativePicker(e.currentTarget)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openNativePicker(e.currentTarget);
          }
        }}
        aria-label="Inspection time — opens a picker"
        className="w-[7.5rem] text-xs pl-7 pr-2 py-1 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-primary-500 cursor-pointer [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-70"
      />
    </div>
  );
}

/**
 * The inspection-date control at the top of the questions phase.
 *
 * Jim 2026-09-04: keep the format-proof date box, but tapping the date should
 * open the calendar (the way the Stage of Construction cells do), the calendar
 * should sit by the field instead of at the far right edge, and the field
 * should not stretch across the whole row on a desktop. So: a compact native
 * date input whose whole surface (and the calendar glyph) opens the picker.
 */
export function InspectionDateField({
  value,
  onChange,
  max,
  id = 'lite-conducted-date',
}: InspectionDateFieldProps) {
  const openPicker = openNativePicker;

  return (
    <div className="relative inline-flex items-center">
      <Calendar
        aria-hidden="true"
        className="pointer-events-none absolute left-2 w-3.5 h-3.5 text-slate-400"
      />
      <input
        id={id}
        type="date"
        value={value}
        max={max ?? todayLocal()}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => openPicker(e.currentTarget)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPicker(e.currentTarget);
          }
        }}
        aria-label="Inspection date — opens a calendar"
        className="w-[10.5rem] text-xs pl-7 pr-2 py-1 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-primary-500 cursor-pointer [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-70"
      />
    </div>
  );
}
